import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Download, Menu, X } from 'lucide-react';
import resumeFile from '../assets/Debojit Resume.pdf';
import { analyticsAPI } from '../utils/api';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/work', label: 'Work' },
    { path: '/about', label: 'About' },
    { path: '/contact', label: 'Contact' }
  ];

  const trackTabClick = (link) => {
    analyticsAPI.trackPageView({
      project_id: '',
      project_title: '',
      page_key: `tab:${link.label.toLowerCase()}`,
      page_title: link.path,
      event_type: 'tab_click',
      tab_name: link.label,
      user_agent: navigator.userAgent,
      ip_address: ''
    }).catch((error) => {
      console.error('Tab click analytics tracking failed:', error);
    });
  };

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glassmorphism' : 'bg-transparent'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <Link 
            to="/" 
            className="text-xl sm:text-2xl font-bold font-['Syne'] tracking-tight whitespace-nowrap hover:text-primary transition-colors"
            data-testid="logo-link"
          >
            Debojit Portfolio
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => trackTabClick(link)}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                  location.pathname === link.path ? 'text-primary' : 'text-white/80'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={resumeFile}
              download="Debojit Resume.pdf"
              className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-4 py-2 text-sm font-medium text-white hover:bg-primary/20 transition-colors"
              data-testid="nav-download-resume-button"
            >
              <Download size={16} />
              Download Resume
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden glassmorphism border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => {
                  trackTabClick(link);
                  setIsMobileMenuOpen(false);
                }}
                className={`block text-sm font-medium ${
                  location.pathname === link.path ? 'text-primary' : 'text-white/80'
                }`}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={resumeFile}
              download="Debojit Resume.pdf"
              onClick={() => setIsMobileMenuOpen(false)}
              className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-4 py-2 text-sm font-medium text-white hover:bg-primary/20 transition-colors"
              data-testid="mobile-nav-download-resume-button"
            >
              <Download size={16} />
              Download Resume
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
